import { MAX_SOURCE_BYTES } from 'shared/utils/image/image-messages';
import UDFormFieldWrapper from 'ud-form/wrapper';
import { UDDropzone } from './component';
import { IMAGE_ACCEPT } from './domain/accepted-formats';
import { useDropzoneField } from './hooks/use-dropzone-field';
import { UDFormDropzoneProps } from './types';

/**
 * Поле формы с загрузкой изображений.
 *
 * Одно на все случаи: фото игрока, логотип команды, аватар в модалке, до пяти
 * фотографий локации. Раньше их обслуживали две дропзоны и «три варианта»
 * внутри одной из них, причём варианты различались не одним признаком, а
 * вперемешку — местом превью, формой значения и тем, какие пропсы вообще
 * доезжают до `react-dropzone`. Здесь всё это обычные пропсы.
 *
 * Подпись, ошибка и подсказка приходят от `UDFormFieldWrapper` — того же, через
 * который проходят `UDFormInput` и `UDFormSelect`. До объединения дропзона была
 * единственным полем кита мимо него: легаси-версия повторяла его логику руками,
 * а новая не показывала ошибку валидации вовсе.
 */
export const UDFormDropzone = (props: UDFormDropzoneProps) => {
  const {
    name,
    containerProps,
    label,
    labelProps,
    required,
    errorVisible,
    tooltip,
    tooltipProps,

    preview = 'below',
    previewSize,
    multiple = false,
    maxFiles,
    sortable,
    removable,
    gallery = true,
    showFileName,

    accept = IMAGE_ACCEPT,
    maxSize = MAX_SOURCE_BYTES,
    compression,
    crop,
    cropAspect,
    disabled,

    title,
    subtitle,
    buttonText,
    onButtonClick,
    className,
  } = props;

  const {
    previews,
    rootProps,
    inputProps,
    isDragActive,
    isFocused,
    isProcessing,
    isDisabled,
    remove,
    reorder,
    cropFile,
    confirmCrop,
    skipCrop,
    cancelCrop,
  } = useDropzoneField({
    name,
    multiple,
    maxFiles,
    accept,
    maxSize,
    compression,
    crop,
    cropAspect,
    disabled,
  });

  return (
    <UDFormFieldWrapper
      name={name}
      containerProps={containerProps}
      label={label}
      labelProps={labelProps}
      required={required}
      errorVisible={errorVisible}
      tooltip={tooltip}
      tooltipProps={tooltipProps}
    >
      {({ meta }) => (
        <UDDropzone
          previews={previews}
          rootProps={rootProps}
          inputProps={inputProps}
          accept={accept}
          maxSize={maxSize}
          preview={preview}
          previewSize={previewSize}
          // Имя файла различает картинки там, где их несколько; у единственного
          // фото оно только шумит рядом с превью.
          showFileName={showFileName ?? multiple}
          // Выключенное поле нельзя не только пополнить, но и разобрать:
          // у локаций им закрывают работу с фото, когда подписка не оплачена.
          sortable={sortable && !disabled}
          removable={removable && !disabled}
          gallery={gallery}
          isDragActive={isDragActive}
          isFocused={isFocused}
          isProcessing={isProcessing}
          isDisabled={isDisabled}
          hasError={(meta.touched || Boolean(meta.value)) && Boolean(meta.error)}
          onRemove={remove}
          onReorder={reorder}
          cropFile={cropFile}
          cropAspect={cropAspect}
          onCrop={confirmCrop}
          onCropSkip={skipCrop}
          onCropCancel={cancelCrop}
          title={title}
          subtitle={subtitle}
          buttonText={buttonText}
          onButtonClick={onButtonClick}
          className={className}
        />
      )}
    </UDFormFieldWrapper>
  );
};

export default UDFormDropzone;
